import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import type { ToolCallInfo } from "@/types/agent";

const TOOL_LABELS: Record<string, string> = {
  search_records: "Searching records",
  get_record: "Looking up record details",
  get_dashboard_stats: "Crunching the numbers",
  get_expiring_records: "Checking upcoming expiries",
  compare_records: "Comparing records side by side",
  get_audit_history: "Reviewing change history",
  search_by_location: "Searching by location",
  propose_changes: "Preparing your changes",
  execute_changes: "Applying changes now",
};

const TOOL_DONE_LABELS: Record<string, string> = {
  search_records: "Found records",
  get_record: "Record loaded",
  get_dashboard_stats: "Stats ready",
  get_expiring_records: "Expiry check done",
  compare_records: "Comparison ready",
  get_audit_history: "History loaded",
  search_by_location: "Location search done",
  propose_changes: "Changes ready for review",
  execute_changes: "Changes applied",
};

interface ToolCallDisplayProps {
  toolCalls: ToolCallInfo[];
}

export function ToolCallDisplay({ toolCalls }: ToolCallDisplayProps): React.ReactElement | null {
  if (toolCalls.length === 0) return null;

  const activeCall = toolCalls.find((tc) => tc.status === "running");
  const hasError = toolCalls.some((tc) => tc.status === "error");
  const allComplete = toolCalls.every((tc) => tc.status === "complete");

  if (activeCall) {
    return (
      <div className="flex items-center gap-2 py-1 text-xs text-muted-foreground" data-testid="agent-tool-status">
        <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
        <span>{TOOL_LABELS[activeCall.toolName] ?? "Working on it"}...</span>
      </div>
    );
  }

  if (hasError) {
    const errorCall = toolCalls.find((tc) => tc.status === "error");
    return (
      <div className="flex items-center gap-2 py-1 text-xs text-destructive" data-testid="agent-tool-status">
        <XCircle className="h-3.5 w-3.5" />
        <span>{errorCall?.summary ?? "Something went wrong"}</span>
      </div>
    );
  }

  if (allComplete) {
    const lastCall = toolCalls[toolCalls.length - 1];
    if (!lastCall) return null;

    const label = lastCall.summary ?? TOOL_DONE_LABELS[lastCall.toolName] ?? "Done";

    return (
      <div className="flex items-center gap-2 py-1 text-xs text-muted-foreground" data-testid="agent-tool-status">
        <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
        <span>{label}</span>
      </div>
    );
  }

  return null;
}
